import React, { useState, useEffect } from 'react';
import config from '../../config/config';
import { FiSave, FiRefreshCw, FiAlertCircle, FiCheckCircle, FiPlusCircle } from 'react-icons/fi';

export function AddHoldAnimal() {
  const [formData, setFormData] = useState({
    animalCode: '',
    type: '',
    category: '',
    slaughteringDay: ''
  });

  const [animalTypes, setAnimalTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState({ types: false, categories: false, submit: false });

  const availableDays = [1, 2, 3];

  // (Fetch logic same rahegi jese aapki thi...)
  useEffect(() => {
    const fetchAnimalTypes = async () => {
      setLoading(prev => ({ ...prev, types: true }));
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${config.baseUrl}/api/subcategories/filteranimal-type`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        setAnimalTypes(Array.isArray(data) ? data : []);
      } catch (err) {
        setError("Failed to load animal types");
      } finally {
        setLoading(prev => ({ ...prev, types: false }));
      }
    };
    fetchAnimalTypes();
  }, []);

  useEffect(() => {
    if (!formData.type) {
      setCategories([]);
      return;
    }
    const fetchSubCategories = async () => {
      setLoading(prev => ({ ...prev, categories: true }));
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${config.baseUrl}/api/subcategories/filtersubcat/${formData.type}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setCategories(Array.isArray(data) ? data : []);
      } catch (err) {
        setError("Failed to load categories");
      } finally {
        setLoading(prev => ({ ...prev, categories: false }));
      }
    };
    fetchSubCategories();
  }, [formData.type]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'type' ? { category: '', animalCode: '' } : {}),
      ...(name === 'category' || name === 'slaughteringDay' ? { animalCode: '' } : {})
    }));
  };

  const handleReset = () => {
    setFormData({ animalCode: '', type: '', category: '', slaughteringDay: '' });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(prev => ({ ...prev, submit: true }));
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.baseUrl}/api/hold/hold-animal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: formData.type,
          category: formData.category,
          slaughteringDay: formData.slaughteringDay,
          animalNumber: formData.animalCode
        })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Failed to hold animal');

      setSuccess('Animal has been placed on hold successfully!');
      setTimeout(() => handleReset(), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(prev => ({ ...prev, submit: false }));
    }
  };

  // Helper styles
  const inputBase = "w-full h-12 px-4 border-2 rounded-2xl bg-white text-slate-700 font-bold transition-all outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/5 placeholder-slate-300";
  const labelBase = "block text-[11px] font-black text-slate-500 uppercase tracking-widest mb-2 ml-1";

  return (
    <main className="flex-1 min-h-screen md:p-10">
      <div className="max-w-4xl mx-auto">
        
        {/* Header Section */}
        <div className="mb-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-black text-slate-900 tracking-tight flex items-center gap-3">
              HOLD <span className="text-indigo-800">ANIMAL</span>
            </h1>
          </div>
        </div>
        <div className="bg-white rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.04)] border border-indigo-100 overflow-hidden relative">
          
          {/* Subtle Accent Bar */}
          <div className="h-2 bg-gradient-to-r from-indigo-900 via-indigo-700 to-indigo-800 w-full" />
          
          <div className="md:p-10 pt-2">
            <form onSubmit={handleSubmit} className="space-y-2">
              
              {/* Status Messages */}
              <div className="space-y-4">
                {error && (
                  <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 text-red-600 rounded-2xl animate-shake">
                    <FiAlertCircle className="flex-shrink-0" size={20} />
                    <p className="text-sm font-black uppercase tracking-tight">{error}</p>
                  </div>
                )}
                {success && (
                  <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-100 text-green-600 rounded-2xl">
                    <FiCheckCircle className="flex-shrink-0" size={20} />
                    <p className="text-sm font-black uppercase tracking-tight">{success}</p>
                  </div>
                )}
              </div>

              {/* Form Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                
                {/* Animal Type */}
                <div className="group">
                  <label className={labelBase}>Animal Type</label>
                  <select 
                    name="type" 
                    value={formData.type} 
                    onChange={handleChange}
                    className={`${inputBase} ${formData.type ? 'border-slate-100' : 'border-slate-100'}`}
                  >
                    <option value="">Select Type</option>
                    {animalTypes.map((t) => (
                      <option key={t.id} value={t.id}>{t.cat_name}</option>
                    ))}
                  </select>
                </div>

                {/* Category */}
                <div className="group">
                  <label className={labelBase}>
                    Category 
                    {loading.categories && <FiRefreshCw className="inline ml-2 animate-spin text-blue-500" size={14} />}
                  </label>
                  <select 
                    name="category" 
                    value={formData.category} 
                    onChange={handleChange}
                    disabled={!formData.type || loading.categories}
                    className={`${inputBase} ${!formData.type ? 'bg-slate-50 cursor-not-allowed border-slate-50 opacity-60' : 'border-slate-100'}`}
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.sub_cat}</option>
                    ))}
                  </select>
                </div>

                {/* Slaughtering Day */}
                <div className="group">
                  <label className={labelBase}>Slaughtering Day</label>
                  <div className="flex gap-3">
                    {availableDays.map(day => (
                      <button
                        key={day}
                        type="button"
                        onClick={() => handleChange({ target: { name: 'slaughteringDay', value: day.toString() } })}
                        className={`flex-1 py-3 rounded-2xl font-black text-sm transition-all border-2 ${
                          formData.slaughteringDay === day.toString() 
                          ? 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-100' 
                          : 'bg-white border-slate-100 text-slate-400 hover:border-blue-100'
                        }`}
                      >
                        DAY {day}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Animal Code */}
                <div className="group">
                  <label className={labelBase}>Animal Number / Code</label>
                  <input 
                    type="number" 
                    name="animalCode" 
                    placeholder="e.g. 102"
                    value={formData.animalCode} 
                    onChange={handleChange} 
                    className={inputBase + " border-slate-100"}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-center gap-4 pt-8 border-t border-slate-50">
                <button 
                  type="submit" 
                  disabled={!formData.animalCode || loading.submit}
                  className="w-full sm:flex-1 flex items-center justify-center gap-2 bg-indigo-800 text-white font-black py-4 px-8 rounded-[1.25rem] hover:bg-indigo-700 active:scale-[0.98] disabled:opacity-30 transition-all shadow-xl shadow-slate-200 uppercase tracking-widest text-xs"
                >
                  {loading.submit ? <FiRefreshCw className="animate-spin" /> : <FiSave size={18} />}
                  Confirm Hold  
                </button>
                
                <button 
                  type="button" 
                  onClick={handleReset} 
                  className="w-full sm:w-auto px-8 py-4 bg-slate-50 text-slate-400 font-black rounded-[1.25rem] hover:bg-red-50 hover:text-red-500 transition-all uppercase tracking-widest text-xs flex items-center justify-center gap-2"
                >
                  <FiRefreshCw /> Reset
                </button>
              </div>

            </form>
          </div>
        </div>
      </div>
    </main>
  );
}